import { apiFetch } from "./http";

export type SavedPost = {
  id: string;
  title: string;
  body: string;
  imageUrl?: string | null;
  cardId?: string | null;
  section: "community" | "adoption";
  status: "pending" | "published" | "hidden";
  createdAt: string;
  author?: string;
  authorId?: string;
  isMine?: boolean;
  moderationReason?: string;
};

export async function createPost(
  token: string,
  input: { title: string; body: string; imageUrl?: string; cardId?: string; section: "pets" | "adoption" },
) {
  return apiFetch<{ post: SavedPost }>("/api/v1/posts", {
    method: "POST",
    headers: { "content-type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ ...input, section: input.section === "adoption" ? "adoption" : "community" }),
  });
}

export async function fetchPublishedPosts() {
  return apiFetch<{ posts: Array<SavedPost & { author: string; authorId: string }> }>("/api/v1/posts");
}

export async function fetchMyPosts(token: string) {
  return apiFetch<{ posts: SavedPost[] }>("/api/v1/me/posts", {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function resubmitPost(
  token: string,
  postId: string,
  input: { title: string; body: string; imageUrl?: string | null; cardId?: string | null; section: "pets" | "adoption" },
) {
  return apiFetch<{ post: SavedPost }>(`/api/v1/posts/${encodeURIComponent(postId)}/resubmit`, {
    method: "POST",
    headers: { "content-type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ ...input, section: input.section === "adoption" ? "adoption" : "community" }),
  });
}

export async function createReply(
  token: string,
  input: { targetId: string; text: string; imageUrl?: string },
) {
  return apiFetch<{ comment: { id: string; targetType: "post"; targetId: string; text: string; imageUrl?: string; status: "pending"; createdAt: string } }>("/api/v1/comments", {
    method: "POST",
    headers: { "content-type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ targetType: "post", ...input }),
  });
}

export async function createServerComment(
  token: string,
  input: { targetType: "card" | "post"; targetId: string; text: string; imageUrl?: string },
) {
  return apiFetch<{ comment: { id: string; targetType: "card" | "post"; targetId: string; text: string; imageUrl?: string; status: "pending"; createdAt: string } }>("/api/v1/comments", {
    method: "POST",
    headers: { "content-type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(input),
  });
}

export async function fetchComments(targetType: "card" | "post", targetId: string) {
  return apiFetch<{ comments: Array<{ id: string; authorId: string; targetId: string; text: string; imageUrl?: string; status: "published"; createdAt: string; author: string }> }>(`/api/v1/comments?targetType=${targetType}&targetId=${encodeURIComponent(targetId)}`);
}

export async function fetchReplies(targetId: string) {
  return apiFetch<{ comments: Array<{ id: string; authorId: string; targetId: string; text: string; imageUrl?: string; status: "published"; createdAt: string; author: string }> }>(`/api/v1/comments?targetType=post&targetId=${encodeURIComponent(targetId)}`);
}

export async function toggleFavorite(token: string, targetType: "card" | "post", targetId: string) {
  const resource = targetType === "card" ? "cards" : "posts";
  return apiFetch<{ saved: boolean }>(`/api/v1/${resource}/${targetId}/favorite`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
}

export async function toggleFollow(token: string, userId: string) {
  return apiFetch<{ following: boolean }>(`/api/v1/users/${userId}/follow`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
}

export async function boostCard(token: string, cardId: string) {
  return apiFetch<{ count: number; card: import("../types").CatCard }>(`/api/v1/cards/${cardId}/boost`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
}

export async function fetchInteractions(token: string) {
  return apiFetch<{ favorites: Array<{ targetType: "card" | "post"; targetId: string }>; follows: Array<{ userId: string }>; boosts: Array<{ cardId: string }>; followerCount: number }>("/api/v1/me/interactions", { headers: { Authorization: `Bearer ${token}` } });
}

export async function deletePost(token: string, postId: string) {
  return apiFetch<{ ok: true; storageCleanupPending: number }>(`/api/v1/posts/${encodeURIComponent(postId)}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
}

export async function deleteComment(token: string, commentId: string) {
  return apiFetch<{ ok: true; storageCleanupPending: number }>(`/api/v1/comments/${encodeURIComponent(commentId)}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
}
